import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';

import { AppRouteBody } from '@/components/AppRouteBody';
import { Entitled } from '@/components/Entitled';
import { createSeedExportConfig } from '@/components/export/configs/seed.export.config';
import {
  buildActiveFilters,
  type SeedKey,
  UniversalExportModal,
} from '@/components/export/universal';
import type { FilterOption, GroupOption } from '@/components/filters/types';
import { FeatureLock } from '@/components/legacy-ui/FeatureLock';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { createContactSupportButton } from '@/components/legacy-ui/utils';
import { Loader } from '@/components/Loader';
import { NoteModal } from '@/components/modals';
import { BulkConfirmationDialog } from '@/components/table/BulkConfirmationDialog';
import { SelectionBanner } from '@/components/table/SelectionBanner';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { useGetSeedFilters } from '@/hooks/api/useGetSeedFilters';
import { useBulkConfirmation } from '@/hooks/useBulkConfirmation/useBulkConfirmation';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useDefaultStatusFilter } from '@/hooks/useDefaultStatusFilter';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import type { SeedFilter } from '@/hooks/useSeeds';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { ImportSeedsModal } from '@/sections/seeds/components/modals/ImportSeedsModal';
import { SeedRemoveTag } from '@/sections/seeds/components/modals/SeedRemoveTag';
import { SeedTag } from '@/sections/seeds/components/modals/SeedTag';
import { SeedsActions } from '@/sections/seeds/components/SeedsActions';
import { SeedsFilters } from '@/sections/seeds/components/SeedsFilters';
import { SeedsHeader } from '@/sections/seeds/components/SeedsHeader';
import { SeedsModals } from '@/sections/seeds/components/SeedsModals';
import { SeedsTable } from '@/sections/seeds/components/SeedsTable';
import {
  useSeedData,
  useSeedFilters,
  useSeedsActions,
  useSeedSearch,
} from '@/sections/seeds/hooks';
import { useSeedCommentHandlers } from '@/sections/seeds/hooks/actions';
import { SeedType } from '@/sections/seeds/types';
import { seedUrlParams } from '@/sections/seeds/utils/seedUrlParams';
import { BulkActivityAnnouncer } from '@/sections/topNavBar/BulkJobsDrawer/BulkActivityAnnouncer';
import { useGlobalState } from '@/state/global.state';
import { Seed, SeedStatus, SeedTypeOption } from '@/types';
import { useEntityFilters } from '@/utils/entityFilters.util';
import { omit } from '@/utils/lodash.util';

function inferTabFromInput(
  seedType: SeedTypeOption,
  textInput: string
): SeedType {
  if (
    seedType === SeedTypeOption.WEB_APPLICATION ||
    seedType === SeedTypeOption.WEB_SERVICE
  ) {
    return 'webapplication';
  }
  const firstSeed = textInput
    .split('\n')
    .map(l => l.trim())
    .find(l => l.length > 0);
  if (firstSeed && /^\d{1,3}\.\d{1,3}\./.test(firstSeed)) {
    return 'ip';
  }
  if (firstSeed && firstSeed.includes(':')) {
    return 'ip';
  }
  return 'domain';
}

const applySeedActiveFilter = (prev: SeedFilter): SeedFilter => ({
  ...prev,
  advancedFilters: {
    ...prev.advancedFilters,
    status: ['A'],
  },
});

const clearSeedActiveFilter = (prev: SeedFilter): SeedFilter => {
  const restAdvanced = omit(prev.advancedFilters, 'status');
  return {
    ...prev,
    advancedFilters:
      Object.keys(restAdvanced).length > 0 ? restAdvanced : undefined,
  };
};

/**
 * Maps SeedStatus to a human-readable bulk action name for the confirmation dialog.
 * Defined outside the component to avoid recreation on every render.
 */
function getBulkActionName(status: SeedStatus): string {
  switch (status) {
    case SeedStatus.Freeze:
      return 'freeze';
    case SeedStatus.Delete:
      return 'delete';
    case SeedStatus.Accept:
    case SeedStatus.AcceptLow:
      return 'approve';
    case SeedStatus.Reject:
      return 'reject';
    default:
      return 'update';
  }
}

export const Seeds = () => {
  const queryClient = useQueryClient();
  const seedExportConfig = useMemo(() => createSeedExportConfig(), []);

  // Use centralized feature lock for web applications tab
  const {
    locked: isWebApplicationsLocked,
    lockTitle: webAppsLockTitle,
    lockDescription: webAppsLockDescription,
    featureName: webAppsFeatureName,
  } = useFeatureLock('seeds.webApplications');

  const { canWriteAssets } = useEntitlements();

  const [type, setType] = useState<SeedType>(() => {
    const urlFilters = seedUrlParams.read();
    return urlFilters.type || 'domain';
  });

  // Filter state management with hybrid persistence (localStorage + URL)
  const {
    filters: filter,
    setFilters: setFilter,
    setSort,
    resetFilters,
  } = useSeedFilters(type);

  // Search with debouncing for header SearchBar
  const { searchTerm, setSearchTerm } = useSeedSearch(
    filter.search || '',
    debouncedTerm => {
      setFilter(prev => ({
        ...prev,
        search: debouncedTerm,
      }));
    }
  );

  const [seedForComment, setSeedForComment] = useState<Seed | null>(null);

  // Coordinated data fetching (seeds + counts + preseed count + totalCount)
  const {
    seeds: apiData,
    error,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    hasNextPage,
    isAllEmpty,
    isFilteredEmpty,
    refetchSeeds,
    isAccountEmpty,
    totalCount: totalDatabaseCount = 0,
    activeQuery,
  } = useSeedData(filter);

  // Apply default "Active" (Approved) status filter on new sessions
  useDefaultStatusFilter<SeedFilter>({
    sessionKey: 'seed-default-active-filter',
    applyFilter: applySeedActiveFilter,
    clearFilter: clearSeedActiveFilter,
    setFilters: setFilter,
    isFilteredEmpty,
    dataStatus: status,
    enabled: true,
  });

  const { data: seedFilterOptions, status: filterOptionsStatus } =
    useGetSeedFilters();
  const { sortedOptions: seedSortedOptions } = useEntityFilters('Seed');
  const seedFieldLabels = useMemo(
    () => Object.fromEntries(seedSortedOptions.map(o => [o.value, o.label])),
    [seedSortedOptions]
  );

  const { modal } = useGlobalState();

  // Selection state management — Gmail-style two-step bulk selection
  const bulkSelection = useBulkSelection({
    data: apiData || [],
    activeFilters: (activeQuery ?? {}) as import('@/types').GraphQuery,
    filterVersion: filter,
  });

  const {
    selectedRows: selectedKeys,
    setSelectedRows: setSelectedKeys,
    hasSelection: isSeedsSelected,
    clearSelection,
    isSelectAll,
    isAllLoadedSelected,
    selectAllMatching,
    selectedCount: bulkSelectedCount,
    state: selectionState,
  } = bulkSelection;

  const exportSelectedSeedKeys = isSelectAll
    ? []
    : (selectedKeys as unknown as readonly SeedKey[]);

  // Derive selectedSeeds from selectedKeys for action handlers
  const selectedSeeds = (apiData || []).filter(s =>
    selectedKeys.includes(s.key)
  );

  // Invalidation function for seed queries
  const invalidateSeeds = () => {
    queryClient.invalidateQueries({
      predicate: query => {
        const queryKey = query.queryKey as string[];
        return queryKey[0] === 'MY' && queryKey[1] === 'seed';
      },
      refetchType: 'active',
    });
  };

  // Bulk confirmation hook for the status_change bulk endpoint.
  // Encapsulates: dialog state + useBulkAction mutation + clearSelection wiring.
  // NOTE: does NOT call queryClient.invalidateQueries — that is handled by
  // useActiveBulkJobs (polls job status and invalidates on terminal state).
  const statusConfirm = useBulkConfirmation({
    resource: 'seed',
    action: 'status_change',
    bulkSelection,
    clearSelection,
  });

  // Bulk confirmation hook for the untag bulk endpoint.
  // Same shape as statusConfirm — uses resource:'seed', action:'untag'.
  // Wire format locked: action='untag', params:{tag}.
  const untagConfirm = useBulkConfirmation({
    resource: 'seed',
    action: 'untag',
    bulkSelection,
    clearSelection,
  });

  // Coordinator hook - composes modal state + action handlers
  const { modalState, modalHandlers, handlers } = useSeedsActions({
    selectedSeeds,
    selectedRowsCount: selectedKeys.length,
    invalidateSeeds,
    setConfirmStatus: () => {},
    selectionState,
    onBulkRequested: (status: SeedStatus) => {
      statusConfirm.openConfirmation({
        action: 'status_change',
        actionName: getBulkActionName(status),
        params: { status },
      });
    },
  });

  // Comment handler hook for direct access to handleAddComment
  const { handleAddComment } = useSeedCommentHandlers();

  const handleComment = async (comment: string) => {
    // Use seedForComment if set (single row comment), otherwise use selectedSeeds (bulk comment)
    const seedsToComment = seedForComment ? [seedForComment] : selectedSeeds;

    if (seedsToComment.length === 0) {
      console.warn('[Seeds] handleComment called with no seeds selected');
      return;
    }

    // Directly call handleAddComment with the correct seeds
    // Note: seedType param is not used (prefixed with _) - function uses type guard internally
    const seedType = type === 'preseed' ? 'domain' : type; // TypeScript requires non-preseed type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await handleAddComment(seedsToComment as any, comment, seedType);
    invalidateSeeds();
    clearSelection();
    setSeedForComment(null);
  };

  // Force table remount on empty → data transition (fixes rendering bug)
  const tableKey = useTableRemountOnTransition(
    isFilteredEmpty,
    apiData?.length || 0
  );

  // Generate stable key based on filter/sort state to force remount on changes
  // This resets scroll position and clears stale virtualizer state
  // Note: search is excluded - debounced at API level, shouldn't trigger remounts
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        filters: filter.advancedFilters,
        sort: filter.sort,
        type: filter.type,
      }),
    [filter.advancedFilters, filter.sort, filter.type]
  );

  // Combine with existing tableKey for empty→data transitions
  const combinedTableKey = `${tableKey}-${filterKey}`;

  // Build export active filters using shared utility (reverse-maps values to labels)
  const exportActiveFilters = useMemo(
    () =>
      buildActiveFilters(
        filter.advancedFilters as Record<string, unknown> | undefined,
        (seedFilterOptions as Record<string, (FilterOption | GroupOption)[]>) ??
          {},
        filter.search,
        seedFieldLabels
      ),
    [filter.search, filter.advancedFilters, seedFilterOptions, seedFieldLabels]
  );

  // Detect initial loading state to prevent table UI flicker
  // Matches the pattern used by Vulnerabilities and Technologies tables
  // Include filterOptionsStatus so the spinner stays until BOTH data AND filter options are ready
  const isInitialLoading =
    status === 'pending' || filterOptionsStatus === 'pending';

  // Keyboard action handler
  function handleKeyboardAction(code: string, data: Seed[]) {
    if (!canWriteAssets) return;
    const keyMap = {
      KeyA: SeedStatus.Accept,
      KeyR: SeedStatus.Reject,
      KeyD: SeedStatus.Delete,
      KeyF: SeedStatus.Freeze,
    };
    const status = keyMap[code as keyof typeof keyMap];
    if (status) {
      modalState.confirmation.open(status, data);
    }
  }

  // Confirmation modal handler
  const handleConfirmStatusChange = async () => {
    if (!modalState.confirmation.data) return;
    const { status } = modalState.confirmation.data;

    // Use the appropriate status handler from coordinator
    switch (status) {
      case SeedStatus.Accept:
        await handlers.handleApprove();
        break;
      case SeedStatus.Reject:
        await handlers.handleReject();
        break;
      case SeedStatus.Freeze:
        await handlers.handleFreeze();
        break;
      case SeedStatus.Delete:
        await handlers.handleDelete();
        break;
    }

    clearSelection();
    modalHandlers.closeConfirmationModal();
  };

  return (
    <AppRouteBody title="Seeds">
      {isInitialLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loader isLoading type="spinner" />
        </div>
      ) : (
        <>
          {/* Conditionally render tabs only when account has seeds */}
          {isAccountEmpty === false && (
            <div className="mb-4">
              <SeedsHeader activeTab={type} onTabChange={setType} />
            </div>
          )}

          {/* Content area with optional lock overlay */}
          <div className="relative flex flex-1 flex-col overflow-hidden">
            <FeatureLock
              locked={type === 'webapplication' && isWebApplicationsLocked}
              title={webAppsLockTitle}
              description={webAppsLockDescription}
              action={createContactSupportButton(webAppsFeatureName)}
            >
              {/* Unified Controls Container: Filters + Selection Bar */}
              {!isAllEmpty && (
                <TableControlsContainer
                  data-testid="seeds-controls"
                  filterLabel={true}
                  filters={
                    <SeedsFilters
                      filters={filter}
                      setFilters={setFilter}
                      seedType={type}
                    />
                  }
                  filtersRight={
                    <SearchBar
                      size="sm"
                      className="h-fit w-64"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={setSearchTerm}
                    />
                  }
                  selectionBar={
                    <>
                      <TableSelectionBar
                        embedded={true}
                        alwaysVisible={true}
                        isVisible={isSeedsSelected}
                        selectedCount={bulkSelectedCount}
                        totalCount={apiData?.length || 0}
                        hasNextPage={hasNextPage}
                        totalDatabaseLabel={`${totalDatabaseCount.toLocaleString()} approved ${type}s`}
                        resourceLabel={`${type}s`}
                        emptySelectionMessage={`Select ${type}s to perform bulk actions`}
                        actions={
                          <Entitled entitlement="write_assets">
                            <SeedsActions
                              seedType={type}
                              selectedSeeds={selectedSeeds}
                              selectedRowsCount={bulkSelectedCount}
                              modalHandlers={modalHandlers}
                              actionHandlers={handlers}
                              onClearSelection={clearSelection}
                            />
                          </Entitled>
                        }
                      />
                      <SelectionBanner
                        selectedCount={bulkSelectedCount}
                        totalLoadedCount={apiData?.length || 0}
                        hasNextPage={hasNextPage ?? false}
                        isSelectAll={isSelectAll}
                        isAllLoadedSelected={isAllLoadedSelected}
                        resourceLabel={`${type}s`}
                        onSelectAllMatching={selectAllMatching}
                        onClearSelection={clearSelection}
                        enableSelectAllMatching={activeQuery != null}
                      />
                    </>
                  }
                />
              )}

              {/* Single table-scoped aria-live region for bulk action status.
                  UX divergence #6: one region per table, NOT one per row. */}
              <BulkActivityAnnouncer />
              {/* Unified table - works for ALL types including preseed */}
              <SeedsTable
                key={`${type}-${combinedTableKey}`}
                seedType={type}
                data={apiData}
                status={status}
                error={error}
                fetchNextPage={fetchNextPage}
                isFetching={isFetching}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={hasNextPage}
                sort={
                  filter.sort && {
                    orderBy: filter.sort.orderBy,
                    descending: filter.sort.descending,
                    onSort: setSort,
                  }
                }
                selectedRows={selectedKeys}
                onSelectionChange={setSelectedKeys}
                onKeyboardAction={handleKeyboardAction}
                onWebCrawl={seedKey => {
                  modal.selectUserAgent.onAssetKeysChange([seedKey]);
                }}
                onCommentClick={
                  canWriteAssets
                    ? seed => {
                        setSeedForComment(seed);
                        modalHandlers.openCommentModal();
                      }
                    : undefined
                }
                isAllEmpty={isAllEmpty}
                onResetFilters={resetFilters}
                onAddSeed={
                  type !== 'preseed'
                    ? modalHandlers.openAddSeedModal
                    : undefined
                }
              />
            </FeatureLock>
          </div>
        </>
      )}
      <SeedsModals
        modalState={modalState}
        seedType={type}
        seedForComment={seedForComment}
        onConfirmStatusChange={handleConfirmStatusChange}
        onRefetchSeeds={(seedTypeOption, textInput) => {
          refetchSeeds();
          if (seedTypeOption && textInput) {
            setType(inferTabFromInput(seedTypeOption, textInput));
          }
        }}
      />
      <NoteModal
        open={modalState.comment.isOpen}
        onClose={() => {
          setSeedForComment(null);
          modalHandlers.closeCommentModal();
        }}
        onSubmit={handleComment}
        resourceType="Seed"
        mode="simple"
        selectedCount={seedForComment ? 1 : bulkSelectedCount}
        presentation="modal"
      />
      <SeedTag
        open={modalState.tag.isOpen}
        onClose={modalHandlers.closeTagModal}
        seedKeys={selectedKeys}
        onSuccess={() => {
          clearSelection();
          refetchSeeds();
        }}
      />
      <SeedRemoveTag
        open={modalState.removeTag.isOpen}
        onClose={modalHandlers.closeRemoveTagModal}
        seedKeys={selectedKeys}
        onSuccess={() => {
          clearSelection();
          refetchSeeds();
        }}
        selectionState={selectionState}
        onBulkRequested={tag =>
          untagConfirm.openConfirmation({
            action: 'untag',
            actionName: 'untag',
            params: { tag },
          })
        }
      />
      <UniversalExportModal
        config={seedExportConfig}
        open={modalState.export.isOpen}
        onClose={modalHandlers.closeExportModal}
        selectedRows={exportSelectedSeedKeys}
        onClearSelection={() => {
          clearSelection();
          modalHandlers.closeExportModal();
        }}
        activeFilters={exportActiveFilters}
        activeQuery={activeQuery}
      />
      <ImportSeedsModal
        open={modalState.import.isOpen}
        onClose={modalHandlers.closeImportModal}
        onImport={() => {
          refetchSeeds();
          modalHandlers.closeImportModal();
        }}
      />
      <BulkConfirmationDialog
        isOpen={statusConfirm.confirmPayload !== null}
        actionName={statusConfirm.confirmPayload?.actionName ?? ''}
        resourceLabel="seeds"
        isSelectAll={selectionState.mode === 'all'}
        itemCount={bulkSelectedCount}
        isPending={statusConfirm.isPending}
        onCancel={statusConfirm.closeConfirmation}
        onConfirm={statusConfirm.submitConfirmed}
      />
      <BulkConfirmationDialog
        isOpen={untagConfirm.confirmPayload !== null}
        actionName={untagConfirm.confirmPayload?.actionName ?? ''}
        resourceLabel="seeds"
        isSelectAll={selectionState.mode === 'all'}
        itemCount={bulkSelectedCount}
        isPending={untagConfirm.isPending}
        onCancel={untagConfirm.closeConfirmation}
        onConfirm={untagConfirm.submitConfirmed}
      />
    </AppRouteBody>
  );
};

// Re-export types needed by other components
export type { SeedFilter } from '@/hooks/useSeeds';
